// The header search, grown into a palette, drawn as the template's search
// dialog (next-shadcn-admin-dashboard: dashboard/_components/header/
// search-dialog.tsx, on components/ui/command.tsx). The top bar holds a
// "Search ⌘K" button; it, or ⌘K / Ctrl+K from anywhere, opens the dialog.
// From two characters the list shows what the term matches, grouped —
// customers, conversations, orders — and opens the chosen row where it lives.
// Enter with nothing to pick still does what the plain field did: the inbox
// with the term applied, which is also the "See all" line.
//
// The box is a launcher, not a filter: it empties itself after every pick, so
// the term lives in exactly one place afterwards — the inbox's own search box
// when the pick led there, the recent-searches list otherwise.
//
// Data: GET /ops/api/search (server/ops/search-api.ts). cmdk's own filtering
// is OFF: the server has already matched, and cmdk would hide a row whose
// label lacks the typed text (a match on an order number). Record:
// docs/testing/ui-migration/029-global-search-palette.md.

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Search, XIcon } from "lucide-react";
import { getActiveTenantSlug, opsFetch } from "../api";
import { ShellIcon } from "./shell/icons";
import { Button } from "./ui/button";
import { Command, CommandDialog, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";

type Customer = {
  contactId: string | null;
  displayName: string | null;
  email: string | null;
  phone: string | null;
  conversationCount: number;
  lastActivityAt: string | null;
};
type Conversation = {
  id: string;
  channel: string | null;
  name: string | null;
  email: string | null;
  preview: string | null;
  status: string | null;
  lastActivityAt: string | null;
};
type Order = { orderName: string; conversationIds: string[]; adminUrl: string | null };
type SearchResult = { customers: Customer[]; conversations: Conversation[]; orders: Order[]; total: number };

// Every row the keyboard can land on, in display order.
type Row =
  | { kind: "customer"; key: string; c: Customer }
  | { kind: "conversation"; key: string; c: Conversation }
  | { kind: "order"; key: string; o: Order }
  | { kind: "seeAll"; key: string; total: number }
  | { kind: "recent"; key: string; term: string };

const MIN_TERM = 2;
const DEBOUNCE_MS = 200;
const RECENT_MAX = 5;

function recentKey(): string {
  return `ops.search.recent:${getActiveTenantSlug() ?? "-"}`;
}
function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(recentKey());
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(arr) ? arr.filter((s): s is string => typeof s === "string").slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}
function pushRecent(term: string): string[] {
  const next = [term, ...readRecent().filter((s) => s.toLowerCase() !== term.toLowerCase())].slice(0, RECENT_MAX);
  try { localStorage.setItem(recentKey(), JSON.stringify(next)); } catch { /* private mode */ }
  return next;
}

function shortDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, sameYear ? { day: "numeric", month: "short" } : { day: "numeric", month: "short", year: "numeric" });
}
function statusLabel(s: string | null): string {
  if (s === "resolved") return "Resolved";
  if (s === "closed") return "Closed";
  if (s === "on_hold") return "On hold";
  if (s === "pending") return "Pending";
  return "Open";
}
function channelGlyph(ch: string | null): string {
  if (ch === "email") return "✉";
  if (ch === "whatsapp" || ch === "sms") return "☎";
  if (ch === "facebook" || ch === "instagram" || ch === "meta") return "◎";
  return "💬";
}
function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export default function GlobalSearchPalette() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [resultTerm, setResultTerm] = useState("");
  const [recent, setRecent] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const term = q.trim();

  // ⌘K from anywhere opens it; again, closes it (the template's toggle).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Opening reads the recent list for the workspace in scope; closing, by any
  // route (Escape, a click outside, ⌘K again, a pick), empties the box, so the
  // next visit starts clean.
  const onOpenChange = (next: boolean) => setOpen(next);
  useEffect(() => {
    if (open) setRecent(readRecent());
    else setQ("");
  }, [open]);

  // Live results, debounced; a reply for a term that is no longer in the box
  // is dropped rather than shown.
  useEffect(() => {
    if (term.length < MIN_TERM) {
      setResult(null);
      setResultTerm("");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await opsFetch<SearchResult>(`/ops/api/search?q=${encodeURIComponent(term)}`);
        if (cancelled) return;
        setResult(r);
        setResultTerm(term);
      } catch {
        if (cancelled) return;
        setResult({ customers: [], conversations: [], orders: [], total: 0 });
        setResultTerm(term);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, [term]);

  // The rows on screen. Only the result for the CURRENT term counts — while a
  // newer term is in flight the old list would mislead, so it is hidden.
  const showing = term.length >= MIN_TERM && result && resultTerm === term ? result : null;
  const rows = useMemo<Row[]>(() => {
    if (term.length < MIN_TERM) return recent.map((r) => ({ kind: "recent", key: `r:${r}`, term: r }));
    if (!showing) return [];
    const out: Row[] = [];
    for (const c of showing.customers) out.push({ kind: "customer", key: `c:${c.contactId ?? c.email ?? c.phone ?? ""}`, c });
    for (const c of showing.conversations) out.push({ kind: "conversation", key: `v:${c.id}`, c });
    for (const o of showing.orders) out.push({ kind: "order", key: `o:${o.orderName}`, o });
    if (showing.total > 0) out.push({ kind: "seeAll", key: "all", total: showing.total });
    return out;
  }, [term, showing, recent]);

  const close = () => setOpen(false);
  const clear = () => { setQ(""); setRecent(readRecent()); inputRef.current?.focus(); };
  const goInbox = (t: string) => {
    if (!t) return;
    setRecent(pushRecent(t));
    navigate(`/conversations?search=${encodeURIComponent(t)}`);
    close();
  };
  const openRow = (row: Row) => {
    if (row.kind === "recent") {
      // The term goes back in the box, and so does the focus: a mouse pick
      // leaves it on the list, where the next keystroke would go nowhere.
      setQ(row.term);
      inputRef.current?.focus();
      return;
    }
    setRecent(pushRecent(term));
    if (row.kind === "conversation") navigate(`/conversations/${encodeURIComponent(row.c.id)}`);
    else if (row.kind === "customer") {
      // No customer page yet: the inbox filtered to their email IS their
      // history, resolved threads included.
      const key = row.c.email ?? row.c.phone ?? row.c.displayName ?? term;
      navigate(`/conversations?search=${encodeURIComponent(key)}`);
    } else if (row.kind === "order") {
      if (row.o.conversationIds.length === 1) navigate(`/conversations/${encodeURIComponent(row.o.conversationIds[0])}`);
      // Several threads: the inbox search on the bare number, which is how
      // the customer typed it — the '#' is Shopify's.
      else navigate(`/conversations?search=${encodeURIComponent(row.o.orderName.replace(/^#/, ""))}`);
    } else navigate(`/conversations?search=${encodeURIComponent(term)}`);
    close();
  };

  const hasRows = rows.length > 0;
  const showEmpty = term.length >= MIN_TERM && showing && !hasRows;

  // Enter with no row to pick (nothing matched, or still searching) opens the
  // inbox search, as the plain field's Enter did. With rows, cmdk picks the
  // highlighted one through its onSelect.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !hasRows && term) {
      e.preventDefault();
      goInbox(term);
    }
  };

  // Screen-reader summary of what the list holds — the visual grouping is
  // headings, which a listbox does not announce.
  const announce = !open
    ? ""
    : term.length < MIN_TERM
      ? (hasRows ? plural(rows.length, "recent search", "recent searches") : "")
      : showing
        ? `${plural(showing.customers.length, "customer", "customers")}, ${plural(showing.conversations.length, "conversation", "conversations")}${showing.orders.length ? `, ${plural(showing.orders.length, "order", "orders")}` : ""}`
        : "Searching";

  const item = (row: Row, body: ReactNode) => (
    <CommandItem key={row.key} value={row.key} onSelect={() => openRow(row)}>
      {body}
    </CommandItem>
  );
  const sub = "truncate text-xs text-muted-foreground";
  const when = "ml-auto shrink-0 text-xs text-muted-foreground";

  return (
    <>
      <Button
        variant="link"
        className="px-0! font-normal text-muted-foreground hover:no-underline"
        title="Search customers, conversations, orders (⌘K)"
        onClick={() => onOpenChange(true)}
      >
        <Search data-icon="inline-start" />
        Search
        <kbd className="inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-medium text-[10px]">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>
      <CommandDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Search"
        description="Search customers, conversations and orders"
      >
        {/* cmdk names the box through aria-labelledby, from this label. */}
        <Command shouldFilter={false} onKeyDown={onKeyDown} label="Search customers, conversations and orders">
          <div className="relative">
            <CommandInput
              ref={inputRef}
              value={q}
              onValueChange={setQ}
              placeholder="Search…"
              aria-label="Search customers, conversations and orders"
            />
            {q && (
              <Button
                variant="ghost"
                size="icon-xs"
                className="absolute top-1/2 right-3 -translate-y-1/2"
                onClick={clear}
                aria-label="Clear search"
                title="Clear"
              >
                <XIcon />
              </Button>
            )}
          </div>
          <div className="sr-only" aria-live="polite">{announce}</div>
          <CommandList label="Search results">
            {term.length < MIN_TERM && hasRows && (
              <CommandGroup heading="Recent">
                {rows.map((row) => row.kind === "recent" && item(row,
                  <>
                    <ShellIcon name="search" size={13} />
                    <span className="truncate">{row.term}</span>
                  </>,
                ))}
              </CommandGroup>
            )}
            {showing && showing.customers.length > 0 && (
              <CommandGroup heading="Customers">
                {rows.map((row) => row.kind === "customer" && item(row,
                  <>
                    <ShellIcon name="customers" size={13} />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate font-medium">{row.c.displayName ?? row.c.email ?? row.c.phone ?? "Customer"}</span>
                      <span className={sub}>
                        {row.c.displayName && row.c.email ? <>{row.c.email} · </> : null}
                        {plural(row.c.conversationCount, "conversation", "conversations")}
                      </span>
                    </span>
                    <span className={when}>{shortDate(row.c.lastActivityAt)}</span>
                  </>,
                ))}
              </CommandGroup>
            )}
            {showing && showing.conversations.length > 0 && (
              <CommandGroup heading="Conversations">
                {rows.map((row) => row.kind === "conversation" && item(row,
                  <>
                    <span className="w-4 shrink-0 text-center text-xs" aria-hidden="true">{channelGlyph(row.c.channel)}</span>
                    <span className="min-w-0 truncate font-medium">
                      {row.c.name || row.c.email || "Anonymous visitor"}
                      {row.c.preview ? <span className="font-normal text-muted-foreground"> — “{row.c.preview}”</span> : null}
                    </span>
                    <span className={when}>{statusLabel(row.c.status)} · {shortDate(row.c.lastActivityAt)}</span>
                  </>,
                ))}
              </CommandGroup>
            )}
            {showing && showing.orders.length > 0 && (
              <CommandGroup heading="Orders">
                {rows.map((row) => row.kind === "order" && item(row,
                  <>
                    <ShellIcon name="box" size={13} />
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate font-mono">{row.o.orderName}</span>
                      <span className={sub}>looked up in {plural(row.o.conversationIds.length, "conversation", "conversations")}</span>
                    </span>
                    {row.o.adminUrl ? (
                      <a
                        className="ml-auto shrink-0 text-xs text-muted-foreground hover:text-foreground"
                        href={row.o.adminUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Open in Shopify ↗
                      </a>
                    ) : null}
                  </>,
                ))}
              </CommandGroup>
            )}
            {showing && showing.total > 0 && (
              <CommandGroup>
                {rows.map((row) => row.kind === "seeAll" && item(row,
                  <span className="font-medium">See all {plural(row.total, "result", "results")} in Conversations →</span>,
                ))}
              </CommandGroup>
            )}
            {showEmpty && <div className="py-6 text-center text-sm">Nothing matches “{term}”. Enter still opens the inbox search.</div>}
            {!showing && term.length >= MIN_TERM && loading && <div className="py-6 text-center text-sm text-muted-foreground">Searching…</div>}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
}
